import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tag } from 'antd';
import {
  clearCustomerCancellations,
  deleteCustomer,
  getSingleCustomers,
} from '../../../../appRedux/actions';
import {
  customerConst,
  filters,
  searchFilters,
  searchInputInitialValues,
} from '../../../../constants/Customer.const';
import { useCustomModal } from '../../../../hooks/useCustomModal';
import { ListTable, ModalView, TableFilters } from '../../../uiComponents';
import { CustomerTableOptions } from '../CustomerTableOptions';
import { NoteModalBody } from '../../Note/NoteCreateModalBody';
import { phoneMask, getFullName } from '../../../../util';
import { CustomerModal } from '../CustomerModalBody';
import { roleConst } from '../../../../constants';
import { ClearCancellationsModalBody } from '../ClearCancellationsModal';

export const CustomerTable = () => {
  const {
    privileges: { clients: privileges = [] },
    role,
  } = useSelector((state) => state.userAuth);
  const { configs } = useSelector((state) => state.configs);
  const dispatch = useDispatch();
  const { modal } = useCustomModal();
  const [modalTitle, setModalTitle] = useState(null);
  const [children, setChildren] = useState(null);
  const [width, setWidth] = useState(1500);
  const isAdmin = useMemo(() => roleConst.superAdmin === role, [role]);
  const strikeLimit = Number(configs?.strikeAppointment) || 3;

  const onDelete = (id) => {
    dispatch(deleteCustomer(id));
  };

  const clearCancellations = (customer, id) => {
    if (customer.unbanned === true) {
      getModal(customer, 'clearCancellations');
      return;
    }

    dispatch(clearCustomerCancellations(id));
  };

  const getModal = (customer, action = null) => {
    modal();

    switch (action) {
      case 'note':
        setModalTitle('Crear Nota');
        setChildren(<NoteModalBody customer={customer.id} />);
        setWidth(1500);
        break;
      case 'customer':
        setModalTitle(getFullName(customer));
        setChildren(<CustomerModal data={customer} />);
        setWidth(1500);
        break;
      case 'clearCancellations':
        setModalTitle(customerConst.unbanClient);
        setChildren(<ClearCancellationsModalBody customer={customer} />);
        setWidth(1000);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: 'ID de cliente',
      sorter: true,
      dataIndex: 'customerId',
      key: 'customerId',
      render: (id, customer) => (
        <Button
          type="link"
          htmlType="button"
          onClick={() => getModal(customer, 'customer')}
          key={`buttonViewCustomer-${id}`}
        >
          {id}
        </Button>
      ),
    },
    {
      title: 'Nombre',
      sorter: true,
      dataIndex: 'name',
      key: 'name',
      render: (name) => name?.toUpperCase(),
    },
    {
      title: 'Apellido Paterno',
      sorter: true,
      dataIndex: 'lastName',
      key: 'lastName',
      render: (lastName) => lastName?.toUpperCase(),
    },
    {
      title: 'Apellido Materno',
      sorter: true,
      dataIndex: 'motherLastName',
      key: 'motherLastName',
      render: (motherLastName) => motherLastName?.toUpperCase(),
    },
    {
      title: 'Puntos',
      sorter: true,
      dataIndex: 'points',
      key: 'points',
      render: (points) => <Tag>{points}</Tag>,
    },
    {
      title: 'Género',
      sorter: true,
      dataIndex: 'gender',
      key: 'gender',
      render: (gender) => gender?.toUpperCase(),
    },
    {
      title: 'Celular',
      sorter: true,
      dataIndex: 'mobilePhone',
      key: 'phone',
      render: (mobilePhone, data) => {
        const { tutor } = data;

        const phone = mobilePhone ?? tutor?.mobilePhone;

        return phone ? phoneMask(phone) : '-';
      },
    },
    {
      title: 'Prefijo',
      sorter: true,
      dataIndex: 'phonePrefix',
      key: 'phonePrefix',
      render: (phonePrefix) => phonePrefix || '-',
    },
    {
      title: 'Nacionalidad',
      sorter: true,
      dataIndex: 'nationality',
      key: 'nationality',
      render: (nationality) => nationality || '-',
    },
    {
      title: 'Opciones',
      key: 'id',
      render: (customer, { id }) => (
        <CustomerTableOptions
          id={id}
          privileges={privileges}
          path="clientes"
          action={{
            onDelete: {
              action: onDelete,
              params: [id],
            },
            onAddNote: {
              getModal,
              params: [customer, 'note'],
            },
            onClearCancellations: {
              action: clearCancellations,
              params: [customer, id],
              canUnban: customer.cancellations >= strikeLimit,
              requiresCode: customer.unbanned === true,
            },
          }}
        />
      ),
    },
  ];

  return (
    <>
      <TableFilters
        searchFilters={searchFilters}
        filters={filters}
        searchInputInitialValues={searchInputInitialValues}
        blackListParam={true}
      />
      <ListTable
        columns={columns}
        getDataFunction={getSingleCustomers}
        canExport={isAdmin}
      />
      <ModalView title={modalTitle} width={width}>
        {children}
      </ModalView>
    </>
  );
};
